import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCompanyBasicStatus } from "../../api/authApi";
import MarketingLayout from "../../marketing/components/MarketingLayout";
import EmailVerificationModal from "../../components/Modal/EmailVerificationModal";

const resolvePortalLandingPath = (profile) => {
  if ((profile?.portalScope || "internal") === "consultant") {
    return "/portal/consultant/activity-reports";
  }
  return "/portal/dashboard";
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, fetchProfile } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      let profile = await login(form.email, form.password);
      let role = (profile?.role || "").toUpperCase();
      if (!profile) {
        try {
          profile = await fetchProfile();
          role = (profile?.role || "").toUpperCase();
        } catch {}
      }

      if (role === "SUPER_ADMIN") {
        navigate("/admin");
        return;
      }

      try {
        const st = (await getCompanyBasicStatus()) || {};
        const accountStatus = st.account_status;
        const days = st.trial_days_remaining;
        const isActive = st.is_active;
        if (accountStatus === "EXPIRED" || (isActive === false && (typeof days === "number" ? days : 0) <= 0)) {
          setError("Votre période d'essai a expiré. Contactez-nous pour renouveler l'accès.");
          logout();
          return;
        }
      } catch (_) {
        // If status check fails, proceed to app; feature routes still guarded
      }

      const from = location?.state?.from;
      const redirectPath =
        (profile?.portalScope || "internal") === "consultant"
          ? resolvePortalLandingPath(profile)
          : (from?.pathname
              ? `${from.pathname}${from.search || ""}${from.hash || ""}`
              : null) || resolvePortalLandingPath(profile);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error(err);
      if (
        err?.response?.data?.detail === "EMAIL_NOT_VERIFIED" ||
        err?.response?.data?.message === "EMAIL_NOT_VERIFIED"
      ) {
        setShowVerificationModal(true);
      } else {
        setError(
          err?.response?.data?.detail ||
            "Impossible de se connecter. Veuillez réessayer."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingLayout showFinalCta={false} variant="auth">
      <Helmet>
        <title>Se connecter - ASAP Hub</title>
        <meta name="description" content="Connectez-vous à votre espace ASAP Hub pour piloter votre ESN à la vitesse de l'IA." />
      </Helmet>

      <section className="mk-contact-section" data-testid="login-page">
        <div className="mk-wrap mk-contact-grid">
          <aside className="mk-reveal">
            <span className="mk-eyebrow">
              <span className="mk-pulse" aria-hidden="true" />
              Espace client
            </span>
            <h1>Bon retour parmi nous</h1>
            <p className="mk-lead">
              Reprenez là où vous vous étiez arrêté : profils, besoins, CV, partenaires. Tous vos outils ASAP Hub réunis dans une seule plateforme.
            </p>
            <div className="mk-info-list">
              <div className="mk-info-item">
                <i aria-hidden="true">✓</i>
                <div><b>100% RGPD</b>Hébergé en France</div>
              </div>
              <div className="mk-info-item">
                <i aria-hidden="true">⚡</i>
                <div><b>Modulaire</b>Activez uniquement les modules dont vous avez besoin</div>
              </div>
            </div>
          </aside>

          <div className="mk-contact-card mk-reveal">
            <form onSubmit={handleSubmit} noValidate>
              <h3>Connectez-vous</h3>

              {location?.state?.passwordResetSuccess && (
                <div className="mk-form-info" role="status">
                  Votre mot de passe a été réinitialisé. Vous pouvez vous connecter.
                </div>
              )}
              {location?.state?.emailVerified && (
                <div className="mk-form-info" role="status">
                  Votre adresse email a été vérifiée. Vous pouvez vous connecter.
                </div>
              )}
              {error && (
                <div className="mk-form-error" role="alert" data-testid="login-error">{error}</div>
              )}

              <div className="mk-field">
                <label htmlFor="email">Email <span className="mk-required">*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="mk-field">
                <label htmlFor="password">Mot de passe <span className="mk-required">*</span></label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  required
                />
              </div>

              <button
                type="submit"
                className="mk-btn mk-btn-primary"
                disabled={isSubmitting}
                style={{ width: "100%", padding: "14px 18px" }}
                data-testid="login-submit"
              >
                {isSubmitting ? "Connexion en cours…" : "Se connecter"}
              </button>

              <p className="mk-auth-help">
                <Link to="/forgot-password">Mot de passe oublié ?</Link>
              </p>
            </form>
          </div>
        </div>
      </section>

      <EmailVerificationModal
        show={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={form.email}
      />
    </MarketingLayout>
  );
};

export default Login;
